import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { mobileSearchService } from '../services/mobileSearchService';
import { mobileObjectDetailService } from '../services/mobileObjectDetailService';
import { useApiError } from '../../shared/hooks/useApiError';
import { MessageDialog } from '../../shared/ui';
import Footer from '../components/Footer';
import { useAuth } from '../../shared/auth';

// CSS-in-JS スタイル定義
const styles = {
  container: {
    minHeight: '100vh',
    background: 'white',
    position: 'relative',
    paddingBottom: '60px'
  },
  loading: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(255, 255, 255, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999
  },
  contents: {
    display: 'block',
    padding: '25px 0px 50px 0px',
    maxWidth: '500px',
    margin: '0 auto'
  },
  searchContainer: {
    margin: '0px 20px'
  },
  input: {
    width: '100%',
    padding: '16px',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    marginBottom: '20px',
    backgroundColor: '#f5f5dc',
    boxSizing: 'border-box',
    textAlign: 'center',
    color: '#333',
    fontWeight: 500,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    minHeight: '80px' // ID検索時の高さを調整
  },
  inputFocus: {
    outline: 'none',
    borderColor: '#F93535',
    boxShadow: '0 0 0 3px rgba(249, 53, 53, 0.2)',
    backgroundColor: '#fff'
  },
  inputPlaceholder: {
    color: '#666',
    fontWeight: 'normal'
  },
  typeContainer: {
    display: 'flex',
    marginBottom: '12px',
    position: 'relative',
    borderRadius: '12px',
    overflow: 'hidden',
    border: '2px solid #e0e0e0',
    background: '#f8f8f8',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
  },
  typeButton: {
    flex: 1,
    padding: '5px 15px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: 500,
    transition: 'all 0.3s ease',
    color: '#666',
    position: 'relative'
  },
  typeButtonActive: {
    background: '#F93535',
    color: 'white',
    fontWeight: 'bold',
    boxShadow: '0 2px 4px rgba(249, 53, 53, 0.3)'
  },
  typeButtonHover: {
    background: 'rgba(249, 53, 53, 0.1)',
    color: '#F93535'
  },
  detailButtonContainer: {
    display: 'flex',
    justifyContent: 'right',
    marginBottom: '20px'
  },
  detailButton: {
    padding: '10px 20px',
    background: '#333',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
  },
  detailButtonHover: {
    background: '#555',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)'
  },
  idContainer: {
    display: 'flex',
    marginBottom: '15px'
  },
  idInput: {
    flex: 1,
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box',
    height: '90px',
    resize: 'vertical',
    fontFamily: 'inherit',
    lineHeight: '1.4'
  },
  rangeContainer: {
    display: 'grid',
    gap: '10px',
    marginBottom: '15px'
  },
  rangeInput: {
    flex: 1,
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box',
    height: '40px'
  },
  rangeInputFocus: {
    outline: 'none',
    borderColor: '#F93535'
  },
  searchButton: {
    width: '100%',
    padding: '10px 18px',
    background: '#F93535',
    color: 'white',
    border: 'none',
    borderRadius: '25px',
    fontSize: '18px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginBottom: '25px',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 12px rgba(249, 53, 53, 0.3)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  searchButtonHover: {
    background: '#e02e2e',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(249, 53, 53, 0.4)'
  },
  searchButtonActive: {
    transform: 'translateY(0)',
    boxShadow: '0 2px 8px rgba(249, 53, 53, 0.3)'
  },
  searchButtonDisabled: {
    background: '#ccc',
    cursor: 'not-allowed',
    transform: 'none',
    boxShadow: 'none'
  },
  detailContainer: {
    border: '1px solid #ddd',
    borderRadius: '8px',
    padding: '15px',
    marginBottom: '15px',
    backgroundColor: '#f9f9f9',
    animation: 'slideDown 0.3s ease-out'
  },
  detailTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '15px',
    color: '#333',
    borderBottom: '2px solid #F93535',
    paddingBottom: '5px'
  },
  formGroup: {
    marginBottom: '15px'
  },
  label: {
    display: 'block',
    marginTop: '10px',
    marginBottom: '5px',
    fontSize: '14px',
    color: '#666',
    fontWeight: 500
  },
  select: {
    width: '100%',
    padding: '8px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    background: 'white',
    boxSizing: 'border-box'
  },
  selectFocus: {
    outline: 'none',
    borderColor: '#F93535'
  },
  datetimeContainer: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center'
  },
  datetimeInput: {
    flex: 1,
    padding: '8px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box'
  },
  datetimeInputFocus: {
    outline: 'none',
    borderColor: '#F93535'
  },
  checkboxContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '15px',
    marginBottom: '15px'
  },
  checkboxItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px'
  },
  checkbox: {
    margin: 0,
    width: '16px',
    height: '16px',
    accentColor: '#F93535'
  },
  checkboxLabel: {
    fontSize: '14px',
    color: '#666',
    cursor: 'pointer'
  },
     objectList: {
     margin: '60px auto 120px auto',
     clear: 'both',
     display: 'grid',
     gridTemplateColumns: 'repeat(3, 1fr)',
     width: '100%',
     boxSizing: 'border-box',
     gap: '0'
   },
   objectDIV: {
     outline: '1px solid #DDD',
     outlineOffset: '-1px',
     background: '#2B2222',
     overflow: 'hidden',
     cursor: 'pointer',
     margin: '0',
     width: '100%',
     height: 'auto',
     boxSizing: 'border-box'
   },
  
  resultTitle: {
    fontSize: '8pt',
    fontWeight: '900',
    color: '#FFF',
    background: '#2B2222',
    padding: '2px 4px',
    height: '20px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis'
  },
  resultInfo: {
    overflow: 'hidden',
    aspectRatio: '1',
    width: '100%'
  },
  loadObjectDataButtonArea: {
    display: 'block',
    textAlign: 'center',
    margin: '120px auto 200px auto'
  },
  loadObjectDataButton: {
    background: '#2B2222',
    color: '#FFF',
    padding: '10px 20px',
    borderRadius: '5px',
    cursor: 'pointer'
  },
  
   clearButton: {
     width: '50%',
     padding: '10px 18px',
     background: '#d8bfd8',
     color: '#666',
     border: '1px solid #c8a2c8',
     borderRadius: '8px',
     fontSize: '14px',
     fontWeight: 500,
     cursor: 'pointer',
     transition: 'all 0.3s ease'
   },
   clearButtonHover: {
     background: '#c8a2c8',
     borderColor: '#b19cd9'
   },
   clearGroup: {
     marginBottom: '0px',
     marginTop: '30px',
     display: 'flex',
     justifyContent: 'right'
   },


 };

// アニメーション用のCSS
const animationStyles = `
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

// スタイルタグを追加
const styleSheet = document.createElement('style');
styleSheet.type = 'text/css';
styleSheet.innerText = animationStyles;
document.head.appendChild(styleSheet);

const AppSearch = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const { pixStorageFlg, objectIdAutoFlg } = useAuth();

  // APIエラーハンドリングフック
  const { 
    handleApiError,
    showErrorDialog,
    errorMessage,
    messageType,
    closeErrorDialog
  } = useApiError(() => setLoading(false));
  
  // UI状態
  const [loading, setLoading] = useState(false); // ローディング状態
  const [searchType, setSearchType] = useState('id'); // 検索タイプ（'id' or 'range'）
  const [showDetailSearch, setShowDetailSearch] = useState(false); // 詳細検索表示フラグ

  // 検索条件
  const [searchParams, setSearchParams] = useState({
    objectId: '', // オブジェクトID
    objectIdFrom: '', // ID範囲検索（開始）
    objectIdTo: '', // ID範囲検索（終了）
    satsueiMemoCategory: '', // 撮影メモカテゴリー
    kyoten: '', // 拠点
    shokiTorokusha: '', // 初期登録者
    shokiTorokuJikokuFrom: '', // 初期登録時刻（開始）
    shokiTorokuJikokuTo: '', // 初期登録時刻（終了）
    shokiTorokuJikokuJokenFrom: '1', // 初期登録時刻条件（開始）
    shokiTorokuJikokuJokenTo: '1', // 初期登録時刻条件（終了）
    saishuKoshinsha: '', // 最終更新者
    saishuKoshinJikokuFrom: '', // 最終更新時刻（開始）
    saishuKoshinJikokuTo: '', // 最終更新時刻（終了）
    saishuKoshinJikokuJokenFrom: '1', // 最終更新時刻条件（開始）
    saishuKoshinJikokuJokenTo: '1', // 最終更新時刻条件（終了）
    sort: '1', // ソート順
    noImageRegist: false, // 画像未撮影フラグ
    imageRegist: false, // 画像撮影済フラグ
    noSatsueiMemoRegist: false // 商品情報未登録フラグ
  });
  
  // マスターデータ
  const [masterData, setMasterData] = useState({
    satsueiMemoCategories: [], // 撮影メモカテゴリー一覧
    kyotenList: [], // 拠点一覧
    userList: [] // ユーザー一覧
  });
  
  // 検索結果
  const [searchResults, setSearchResults] = useState([]); // 検索結果一覧
  const [hasMore, setHasMore] = useState(false); // 追加読み込み可能フラグ
  const [offset, setOffset] = useState(0); // オフセット
  const limit = 60; // 1回の検索で取得する件数
  
  // スクロール位置管理
  const [savedScrollPosition, setSavedScrollPosition] = useState(null); // 保存されたスクロール位置
  
  // 画像URLの管理（blob用）
  const [imageUrls, setImageUrls] = useState({}); // 画像URLキャッシュ

  // コンポーネント初期化
  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      // 初期化時に画像URLをクリア
      setImageUrls({});
      try {
        // マスターデータを並行して取得
        const [
          satsueiMemoCategories,
          kyotenList,
          userList
        ] = await Promise.all([
          mobileSearchService.getSatsueiMemoCategory(),
          mobileSearchService.getKyotenList(),
          mobileSearchService.getPixUserList()
        ]);
        
        setMasterData({
          satsueiMemoCategories: Array.isArray(satsueiMemoCategories.satsueiMemoCategoryList) ? satsueiMemoCategories.satsueiMemoCategoryList : [],
          kyotenList: Array.isArray(kyotenList.kyotenList) ? kyotenList.kyotenList : [],
          userList: Array.isArray(userList.pixUserList) ? userList.pixUserList : [],
        });
        
      } catch (error) {
        handleApiError(new Error('初期化に失敗しました。'));
      } finally {
        setLoading(false);
      }
    };
    
    initializeData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // スクロール位置復元処理
  useEffect(() => {
    if (savedScrollPosition !== null) {
      const listContainer = document.querySelector('[data-scroll-container]');
      if (listContainer) {
        // 複数のフレームで試行して確実に復元
        const restoreScroll = () => {
          listContainer.scrollTop = savedScrollPosition;
          setSavedScrollPosition(null);
        };
        
        requestAnimationFrame(() => {
          requestAnimationFrame(restoreScroll);
        });
      }
    }
  }, [searchResults, savedScrollPosition]);

  // URLパラメータ処理
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const objectId = urlParams.get('objectId');
    const from = urlParams.get('from');

    // 詳細画面から戻ってきた場合: sessionStorageから検索条件を復元して再検索
    if (from === 'search') {
      const savedParams = sessionStorage.getItem('appSearch_params');
      const savedType = sessionStorage.getItem('appSearch_type');
      const savedShowDetail = sessionStorage.getItem('appSearch_showDetail');
      if (savedParams) {
        const restored = JSON.parse(savedParams);
        setSearchParams(restored);
        if (savedType) setSearchType(savedType);
        if (savedShowDetail === '1') setShowDetailSearch(true);
        handleSearch(false, restored);
        return;
      }
    }

    if (objectId) {
      // 外部からobjectId指定で開かれた場合: IDのみで検索
      setSearchType('id');
      setSearchParams(prev => ({
        ...prev,
        objectId: objectId
      }));
      const customSearchParams = {
        objectId: objectId,
        objectIdFrom: '',
        objectIdTo: '',
        satsueiMemoCategory: '',
        kyoten: '',
        shokiTorokusha: '',
        shokiTorokuJikokuFrom: '',
        shokiTorokuJikokuTo: '',
        shokiTorokuJikokuJokenFrom: '1',
        shokiTorokuJikokuJokenTo: '1',
        saishuKoshinsha: '',
        saishuKoshinJikokuFrom: '',
        saishuKoshinJikokuTo: '',
        saishuKoshinJikokuJokenFrom: '1',
        saishuKoshinJikokuJokenTo: '1',
        sort: '1',
        noImageRegist: false,
        imageRegist: false,
        noSatsueiMemoRegist: false
      };
      handleSearch(false, customSearchParams);
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // 検索実行処理
  const handleSearch = useCallback(async (isLoadMore = false, customParams = null) => {
    // バリデーション: 日時指定が選択されているが日時が入力されていない場合
    if ((searchParams.shokiTorokuJikokuJokenFrom === '5' && !searchParams.shokiTorokuJikokuFrom) ||
        (searchParams.shokiTorokuJikokuJokenTo === '5' && !searchParams.shokiTorokuJikokuTo) ||
        (searchParams.saishuKoshinJikokuJokenFrom === '5' && !searchParams.saishuKoshinJikokuFrom) ||
        (searchParams.saishuKoshinJikokuJokenTo === '5' && !searchParams.saishuKoshinJikokuTo)) {
      MessageDialog.show('エラー', '日時を入力してください。', null, 'error');
      return;
    }

    // 検索ボタン押下時（isLoadMore = false かつ customParams = null）は詳細検索ブロックを閉じる
    if (!isLoadMore && !customParams) {
      setShowDetailSearch(false);
    }

    setLoading(true);
    try {
      const currentOffset = isLoadMore ? offset : 0;
      
      // カスタムパラメータが指定されている場合はそれを使用、そうでなければ現在の状態を使用
      const paramsToUse = customParams || searchParams;
      const searchTypeToUse = customParams ? 'id' : searchType;
      
      const params = {
        objectId: (searchTypeToUse === 'id' ? paramsToUse.objectId : null),
        objectIdFrom: (searchTypeToUse === 'range' ? paramsToUse.objectIdFrom : null),
        objectIdTo: (searchTypeToUse === 'range' ? paramsToUse.objectIdTo : null),
        satsueiMemoCategoryId: paramsToUse.satsueiMemoCategory,
        kyotenId: paramsToUse.kyoten,
        shokiTorokusha: paramsToUse.shokiTorokusha,
        shokiTorokuJikokuDateFrom: paramsToUse.shokiTorokuJikokuFrom,
        shokiTorokuJikokuJokenFrom: paramsToUse.shokiTorokuJikokuJokenFrom,
        shokiTorokuJikokuDateTo: paramsToUse.shokiTorokuJikokuTo,
        shokiTorokuJikokuJokenTo: paramsToUse.shokiTorokuJikokuJokenTo,
        saishuKoshinsha: paramsToUse.saishuKoshinsha,
        saishuKoshinJikokuDateFrom: paramsToUse.saishuKoshinJikokuFrom,
        saishuKoshinJikokuJokenFrom: paramsToUse.saishuKoshinJikokuJokenFrom,
        saishuKoshinJikokuDateTo: paramsToUse.saishuKoshinJikokuTo,
        saishuKoshinJikokuJokenTo: paramsToUse.saishuKoshinJikokuJokenTo,
        sort: paramsToUse.sort,
        noSatsueiMemoRegist: paramsToUse.noSatsueiMemoRegist,
        noImageRegist: paramsToUse.noImageRegist,
        imageRegist: paramsToUse.imageRegist,
        limit,
        offset: currentOffset
      };
      
      const results = await mobileSearchService.searchObjectData(params);
      
      // ビジネスロジックエラーのチェック
      if (results.returnCode !== '200') {
        MessageDialog.show('エラー', '検索処理に失敗しました。', null, 'error');
        setLoading(false);
        return;
      }
      
      // APIレスポンスの構造に合わせて処理
      const objectDatas = results.objectDatas || [];
      const objectCount = results.objectCount || 0;
      
      // objectIdで重複を除去し、orderIndex=0の画像のみを表示
      const uniqueObjectDatas = (objectDatas || []).reduce((acc, current) => {
        const existingIndex = acc.findIndex(item => item.objectId === current.objectId);
        if (existingIndex === -1) {
          // 新しいobjectIdの場合、orderIndex=0の画像のみを追加（orderIndexが存在しない場合は全て追加）
          if (current.orderIndex === 0 || current.orderIndex === undefined) {
            acc.push(current);
          }
        } else {
          // 既存のobjectIdの場合、orderIndex=0の画像で置き換え（orderIndexが存在しない場合は全て置き換え）
          if (current.orderIndex === 0 || current.orderIndex === undefined) {
            acc[existingIndex] = current;
          }
        }
        return acc;
      }, []);
      
      if (isLoadMore) {
        setSearchResults(prev => {
          // 既存の結果と新しい結果をマージし、重複を除去
          const allResults = [...prev, ...uniqueObjectDatas];
          const mergedResults = allResults.reduce((acc, current) => {
            const existingIndex = acc.findIndex(item => item.objectId === current.objectId);
            if (existingIndex === -1) {
              acc.push(current);
            } else {
              // 既存のobjectIdの場合、orderIndex=0の画像で置き換え（orderIndexが存在しない場合は全て置き換え）
              if (current.orderIndex === 0 || current.orderIndex === undefined) {
                acc[existingIndex] = current;
              }
            }
            return acc;
          }, []);
          return mergedResults;
        });
      } else {
        setSearchResults(uniqueObjectDatas);
        // 新しい検索の場合は画像URLもクリア
        setImageUrls({});
      }
      
      // 画像をblobとして取得（結果がある場合のみ）
      if (uniqueObjectDatas.length > 0) {
        await loadImagesAsBlobs(uniqueObjectDatas);
      }
      
      setHasMore(objectCount > (currentOffset + limit));
      setOffset(currentOffset + limit);
      
    } catch (error) {
      handleApiError(new Error('検索処理に失敗しました。'));
      // エラーが発生した場合はoffsetをリセット
      setOffset(0);
    } finally {
      setLoading(false);
    }
  }, [searchParams, offset, handleApiError, searchType]);

  // 検索条件変更処理
  const handleSearchParamChange = (field, value) => {
    setSearchParams(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 検索タイプ変更処理
  const handleSearchTypeChange = (type) => {
    setSearchType(type);
    setOffset(0);
  };

  // 詳細検索表示切り替え
  const toggleDetailSearch = () => {
    setShowDetailSearch(!showDetailSearch);
  };

  // 検索結果クリック処理
  const handleResultClick = (objectId) => {
    // 検索条件を保存（詳細画面から戻った時に復元するため）
    sessionStorage.setItem('appSearch_params', JSON.stringify(searchParams));
    sessionStorage.setItem('appSearch_type', searchType);
    sessionStorage.setItem('appSearch_showDetail', showDetailSearch ? '1' : '0');
    navigate(`/appObjectDetail?objectId=${objectId}&objectIdAutoFlg=${objectIdAutoFlg}&from=search`);
  };

  // 追加読み込み処理
  const handleLoadMore = async () => {
    // 現在のスクロール位置を保存
    const listContainer = document.querySelector('[data-scroll-container]');
    const currentScrollTop = listContainer ? listContainer.scrollTop : 0;
    
    try {
      // ローディング状態を設定せずに直接データを取得
      const result = await mobileSearchService.searchObjectData({
        objectId: (searchType === 'id' ? searchParams.objectId : null),
        objectIdFrom: (searchType === 'range' ? searchParams.objectIdFrom : null),
        objectIdTo: (searchType === 'range' ? searchParams.objectIdTo : null),
        satsueiMemoCategoryId: searchParams.satsueiMemoCategory,
        kyotenId: searchParams.kyoten,
        shokiTorokusha: searchParams.shokiTorokusha,
        shokiTorokuJikokuDateFrom: searchParams.shokiTorokuJikokuFrom,
        shokiTorokuJikokuJokenFrom: searchParams.shokiTorokuJikokuJokenFrom,
        shokiTorokuJikokuDateTo: searchParams.shokiTorokuJikokuTo,
        shokiTorokuJikokuJokenTo: searchParams.shokiTorokuJikokuJokenTo,
        saishuKoshinsha: searchParams.saishuKoshinsha,
        saishuKoshinJikokuDateFrom: searchParams.saishuKoshinJikokuFrom,
        saishuKoshinJikokuJokenFrom: searchParams.saishuKoshinJikokuJokenFrom,
        saishuKoshinJikokuDateTo: searchParams.saishuKoshinJikokuTo,
        saishuKoshinJikokuJokenTo: searchParams.saishuKoshinJikokuJokenTo,
        sort: searchParams.sort,
        noSatsueiMemoRegist: searchParams.noSatsueiMemoRegist,
        noImageRegist: searchParams.noImageRegist,
        imageRegist: searchParams.imageRegist,
        limit,
        offset
      });

      if(result.returnCode !== '200'){
        MessageDialog.show('エラー', '検索処理に失敗しました。', null, 'error');
        return;
      }
      
      // APIレスポンスの構造に合わせて処理
      const objectDatas = result.objectDatas || [];
      const objectCount = result.objectCount || 0;
      
      // objectIdで重複を除去し、orderIndex=0の画像のみを表示
      const uniqueObjectDatas = (objectDatas || []).reduce((acc, current) => {
        const existingIndex = acc.findIndex(item => item.objectId === current.objectId);
        if (existingIndex === -1) {
          // 新しいobjectIdの場合、orderIndex=0の画像のみを追加（orderIndexが存在しない場合は全て追加）
          if (current.orderIndex === 0 || current.orderIndex === undefined) {
            acc.push(current);
          }
        } else {
          // 既存のobjectIdの場合、orderIndex=0の画像で置き換え（orderIndexが存在しない場合は全て置き換え）
          if (current.orderIndex === 0 || current.orderIndex === undefined) {
            acc[existingIndex] = current;
          }
        }
        return acc;
      }, []);
      
      if (uniqueObjectDatas.length > 0) {
        // スクロール位置を保存
        setSavedScrollPosition(currentScrollTop);
        
        // 画像をblobとして取得（先に画像を読み込む）
        await loadImagesAsBlobs(uniqueObjectDatas);
        
        // 画像読み込み完了後に検索結果を更新
        setSearchResults(prev => {
          const allResults = [...prev, ...uniqueObjectDatas];
          const mergedResults = allResults.reduce((acc, current) => {
            const existingIndex = acc.findIndex(item => item.objectId === current.objectId);
            if (existingIndex === -1) {
              acc.push(current);
            } else {
              // 既存のobjectIdの場合、orderIndex=0の画像で置き換え（orderIndexが存在しない場合は全て置き換え）
              if (current.orderIndex === 0 || current.orderIndex === undefined) {
                acc[existingIndex] = current;
              }
            }
            return acc;
          }, []);
          return mergedResults;
        });
      }
      
      // offsetを更新
      const newOffset = offset + limit;
      setOffset(newOffset);
      
      // さらに読み込むボタンの表示状態を更新
      const shouldShowLoadMore = parseInt(objectCount) > newOffset;
      setHasMore(shouldShowLoadMore);
      
    } catch (error) {
      handleApiError(new Error('検索処理に失敗しました。'));
    }
  };

  // 画像blob取得処理
  const loadImagesAsBlobs = async (objectDatas) => {
    const imagePromises = objectDatas.map(async (result) => {
      if (result.imageCount > 0 && result.objectImageId !== undefined 
        && result.objectImageId !== null && result.objectImageId !== '') {
        try {
          const blobUrl = await mobileObjectDetailService.getImageAsBlob(
            result.objectId,
            result.objectImageId,
            result.maxObjectImageEditId,
            result.filenameExtension,
            result.isUploaded
          );
          return { objectId: result.objectId, blobUrl };
        } catch (error) {
          return { objectId: result.objectId, blobUrl: '/core/img/errorimage.png' };
        }
      }
      return { objectId: result.objectId, blobUrl: '/core/img/misatsuei.png' };
    });

    try {
      const imageResults = await Promise.all(imagePromises);
      const newImageUrls = {};
      imageResults.forEach(({ objectId, blobUrl }) => {
        newImageUrls[objectId] = blobUrl;
      });
      
      setImageUrls(prev => ({ ...prev, ...newImageUrls }));
    } catch (error) {
      // 画像読み込みエラーは無視
    }
  };

  return (
    <div style={styles.container}>
      {loading && (
        <div style={styles.loading}>
          <div>読み込み中...</div>
        </div>
      )}
      
      {/* メインコンテンツ */}
      {!loading && (
        <div style={styles.contents}>
        <div style={styles.searchContainer}>
          {/* ID入力フィールド */}
          {searchType === 'id' && (
            <div style={styles.idContainer}>
          <textarea
            placeholder="ID"
            data-testid="id-search-input"
            value={searchParams.objectId}
            onChange={(e) => handleSearchParamChange('objectId', e.target.value)}
            style={styles.idInput}
            onFocus={(e) => {
              e.target.style.outline = styles.rangeInputFocus.outline;
              e.target.style.borderColor = styles.rangeInputFocus.borderColor;
            }}
            onBlur={(e) => {
              e.target.style.outline = '';
              e.target.style.borderColor = '';
            }}
          />
            </div>
          )}
          
          {/* ID範囲検索フィールド */}
          {searchType === 'range' && (
            <div style={styles.rangeContainer}>
              <input
                type="text"
                placeholder="FROM"
                value={searchParams.objectIdFrom}
                onChange={(e) => handleSearchParamChange('objectIdFrom', e.target.value)}
                style={styles.rangeInput}
                onFocus={(e) => {
                  e.target.style.outline = styles.rangeInputFocus.outline;
                  e.target.style.borderColor = styles.rangeInputFocus.borderColor;
                }}
                onBlur={(e) => {
                  e.target.style.outline = '';
                  e.target.style.borderColor = '';
                }}
              />
              <input
                type="text"
                placeholder="TO"
                value={searchParams.objectIdTo}
                onChange={(e) => handleSearchParamChange('objectIdTo', e.target.value)}
                style={styles.rangeInput}
                onFocus={(e) => {
                  e.target.style.outline = styles.rangeInputFocus.outline;
                  e.target.style.borderColor = styles.rangeInputFocus.borderColor;
                }}
                onBlur={(e) => {
                  e.target.style.outline = '';
                  e.target.style.borderColor = '';
                }}
              />
            </div>
          )}
          
          {/* 検索タイプ選択 */}
          <div style={styles.typeContainer}>
            <button
              style={{
                ...styles.typeButton,
                ...(searchType === 'id' ? styles.typeButtonActive : {})
              }}
              onClick={() => handleSearchTypeChange('id')}
              onMouseEnter={(e) => {
                if (searchType !== 'id') {
                  e.target.style.background = styles.typeButtonHover.background;
                  e.target.style.color = styles.typeButtonHover.color;
                }
              }}
              onMouseLeave={(e) => {
                if (searchType !== 'id') {
                  e.target.style.background = styles.typeButton.background;
                  e.target.style.color = styles.typeButton.color;
                }
              }}
            >
              ID検索
            </button>
            <button
              style={{
                ...styles.typeButton,
                ...(searchType === 'range' ? styles.typeButtonActive : {})
              }}
              onClick={() => handleSearchTypeChange('range')}
              onMouseEnter={(e) => {
                if (searchType !== 'range') {
                  e.target.style.background = styles.typeButtonHover.background;
                  e.target.style.color = styles.typeButtonHover.color;
                }
              }}
              onMouseLeave={(e) => {
                if (searchType !== 'range') {
                  e.target.style.background = styles.typeButton.background;
                  e.target.style.color = styles.typeButton.color;
                }
              }}
            >
              ID範囲検索
            </button>
          </div>

          {/* 詳細条件ボタン */}
          {pixStorageFlg === 1 && (
            <div style={styles.detailButtonContainer}>
              <button
                style={styles.detailButton}
                onClick={toggleDetailSearch}
                onMouseEnter={(e) => {
                  e.target.style.background = styles.detailButtonHover.background;
                  e.target.style.transform = styles.detailButtonHover.transform;
                  e.target.style.boxShadow = styles.detailButtonHover.boxShadow;
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = styles.detailButton.background;
                  e.target.style.transform = '';
                  e.target.style.boxShadow = styles.detailButton.boxShadow;
                }}
              >
                詳細条件
              </button>
            </div>
          )}
          
          {/* 詳細検索条件 */}
          {showDetailSearch && (
            <div style={styles.detailContainer}>
              <div style={styles.detailTitle}>詳細検索条件</div>
              
              {/* 撮影カテゴリー */}
              <div style={styles.formGroup}>
                <label style={styles.label}>撮影カテゴリー</label>
                <select
                  data-testid="satsuei-memo-category-select"
                  value={searchParams.satsueiMemoCategory}
                  onChange={(e) => handleSearchParamChange('satsueiMemoCategory', e.target.value)}
                  style={styles.select}
                  onFocus={(e) => {
                    e.target.style.outline = styles.selectFocus.outline;
                    e.target.style.borderColor = styles.selectFocus.borderColor;
                  }}
                  onBlur={(e) => {
                    e.target.style.outline = '';
                    e.target.style.borderColor = '';
                  }}
                >
                                      <option value="">選択してください</option>
                    {Array.isArray(masterData.satsueiMemoCategories) && masterData.satsueiMemoCategories.map(category => (
                      <option key={category.satsueiMemoCategoryId} value={category.satsueiMemoCategoryId}>
                        {category.satsueiMemoCategoryName}
                      </option>
                    ))}
                </select>
              </div>
              
              {/* 拠点 */}
              <div style={styles.formGroup}>
                <label style={styles.label}>拠点</label>
                <select
                  data-testid="kyoten-select"
                  value={searchParams.kyoten}
                  onChange={(e) => handleSearchParamChange('kyoten', e.target.value)}
                  style={styles.select}
                  onFocus={(e) => {
                    e.target.style.outline = styles.selectFocus.outline;
                    e.target.style.borderColor = styles.selectFocus.borderColor;
                  }}
                  onBlur={(e) => {
                    e.target.style.outline = '';
                    e.target.style.borderColor = '';
                  }}
                >
                                      <option value="">選択してください</option>
                    {Array.isArray(masterData.kyotenList) && masterData.kyotenList.map(kyoten => (
                      <option key={kyoten.kyotenId} value={kyoten.kyotenId}>
                        {kyoten.kyotenName}
                      </option>
                    ))}
                </select>
              </div>
              
              {/* 初期登録条件 */}
              <div style={styles.formGroup}>
                <div style={styles.detailTitle}>初期登録条件</div>
                <label style={styles.label}>初期登録者</label>
                                  <select
                    data-testid="shoki-torokusha-select"
                    value={searchParams.shokiTorokusha}
                    onChange={(e) => handleSearchParamChange('shokiTorokusha', e.target.value)}
                    style={styles.select}
                    onFocus={(e) => {
                      e.target.style.outline = styles.selectFocus.outline;
                      e.target.style.borderColor = styles.selectFocus.borderColor;
                    }}
                    onBlur={(e) => {
                      e.target.style.outline = '';
                      e.target.style.borderColor = '';
                    }}
                  >
                    <option value="">選択してください</option>
                    {Array.isArray(masterData.userList) && masterData.userList.map(user => (
                      <option key={user.pixUserId} value={user.pixUserId}>
                        {user.userName}
                      </option>
                    ))}
                  </select>
                
                  <label style={styles.label}>初期登録時刻</label>
                 
                 {/* FROM条件 */}
                 <div style={styles.datetimeContainer}>
                   {searchParams.shokiTorokuJikokuJokenFrom === '5' && (
                     <input
                       data-testid="shoki-toroku-jikoku-from-input"
                       type="datetime-local"
                       value={searchParams.shokiTorokuJikokuFrom}
                       onChange={(e) => handleSearchParamChange('shokiTorokuJikokuFrom', e.target.value)}
                       style={styles.datetimeInput}
                       onFocus={(e) => {
                         e.target.style.outline = styles.datetimeInputFocus.outline;
                         e.target.style.borderColor = styles.datetimeInputFocus.borderColor;
                       }}
                       onBlur={(e) => {
                         e.target.style.outline = '';
                         e.target.style.borderColor = '';
                       }}
                     />
                   )}
                   <select
                     data-testid="shoki-toroku-jikoku-joken-from-select"
                     value={searchParams.shokiTorokuJikokuJokenFrom}
                     onChange={(e) => handleSearchParamChange('shokiTorokuJikokuJokenFrom', e.target.value)}
                     style={styles.select}
                     onFocus={(e) => {
                       e.target.style.outline = styles.selectFocus.outline;
                       e.target.style.borderColor = styles.selectFocus.borderColor;
                     }}
                     onBlur={(e) => {
                       e.target.style.outline = '';
                       e.target.style.borderColor = '';
                     }}
                   >
                     <option value="1">指定なし</option>
                     <option value="5">日時指定から</option>
                   </select>
                 </div>
                 
                 <span style={{ display: 'block', textAlign: 'center', margin: '5px 0' }}>〜</span>
                 
                 {/* TO条件 */}
                 <div style={styles.datetimeContainer}>
                   {searchParams.shokiTorokuJikokuJokenTo === '5' && (
                     <input
                       data-testid="shoki-toroku-jikoku-to-input"
                       type="datetime-local"
                       value={searchParams.shokiTorokuJikokuTo}
                       onChange={(e) => handleSearchParamChange('shokiTorokuJikokuTo', e.target.value)}
                       style={styles.datetimeInput}
                       onFocus={(e) => {
                         e.target.style.outline = styles.datetimeInputFocus.outline;
                         e.target.style.borderColor = styles.datetimeInputFocus.borderColor;
                       }}
                       onBlur={(e) => {
                         e.target.style.outline = '';
                         e.target.style.borderColor = '';
                       }}
                     />
                   )}
                   <select
                     data-testid="shoki-toroku-jikoku-joken-to-select"
                     value={searchParams.shokiTorokuJikokuJokenTo}
                     onChange={(e) => handleSearchParamChange('shokiTorokuJikokuJokenTo', e.target.value)}
                     style={styles.select}
                     onFocus={(e) => {
                       e.target.style.outline = styles.selectFocus.outline;
                       e.target.style.borderColor = styles.selectFocus.borderColor;
                     }}
                     onBlur={(e) => {
                       e.target.style.outline = '';
                       e.target.style.borderColor = '';
                     }}
                   >
                     <option value="1">指定なし</option>
                     <option value="2">現在時刻まで</option>
                     <option value="5">日時指定まで</option>
                   </select>
                 </div>
              </div>
              
              {/* 最終更新条件 */}
              <div style={styles.formGroup}>
                <div style={styles.detailTitle}>最終更新条件</div>
                <label style={styles.label}>最終更新者</label>
                                  <select
                    data-testid="saishu-koshinsha-select"
                    value={searchParams.saishuKoshinsha}
                    onChange={(e) => handleSearchParamChange('saishuKoshinsha', e.target.value)}
                    style={styles.select}
                    onFocus={(e) => {
                      e.target.style.outline = styles.selectFocus.outline;
                      e.target.style.borderColor = styles.selectFocus.borderColor;
                    }}
                    onBlur={(e) => {
                      e.target.style.outline = '';
                      e.target.style.borderColor = '';
                    }}
                  >
                    <option value="">選択してください</option>
                    {Array.isArray(masterData.userList) && masterData.userList.map(user => (
                      <option key={user.pixUserId} value={user.pixUserId}>
                        {user.userName}
                      </option>
                    ))}
                  </select>
                
                                 <label style={styles.label}>最終更新時刻</label>
                 
                 {/* FROM条件 */}
                 <div style={styles.datetimeContainer}>
                   {searchParams.saishuKoshinJikokuJokenFrom === '5' && (
                     <input
                       data-testid="saishu-koshin-jikoku-from-input"
                       type="datetime-local"
                       value={searchParams.saishuKoshinJikokuFrom}
                       onChange={(e) => handleSearchParamChange('saishuKoshinJikokuFrom', e.target.value)}
                       style={styles.datetimeInput}
                       onFocus={(e) => {
                         e.target.style.outline = styles.datetimeInputFocus.outline;
                         e.target.style.borderColor = styles.datetimeInputFocus.borderColor;
                       }}
                       onBlur={(e) => {
                         e.target.style.outline = '';
                         e.target.style.borderColor = '';
                       }}
                     />
                   )}
                   <select
                     data-testid="saishu-koshin-jikoku-joken-from-select"
                     value={searchParams.saishuKoshinJikokuJokenFrom}
                     onChange={(e) => handleSearchParamChange('saishuKoshinJikokuJokenFrom', e.target.value)}
                     style={styles.select}
                     onFocus={(e) => {
                       e.target.style.outline = styles.selectFocus.outline;
                       e.target.style.borderColor = styles.selectFocus.borderColor;
                     }}
                     onBlur={(e) => {
                       e.target.style.outline = '';
                       e.target.style.borderColor = '';
                     }}
                   >
                     <option value="1">指定なし</option>
                     <option value="5">日時指定から</option>
                   </select>
                 </div>
                 
                 <span style={{ display: 'block', textAlign: 'center', margin: '5px 0' }}>〜</span>
                 
                 {/* TO条件 */}
                 <div style={styles.datetimeContainer}>
                   {searchParams.saishuKoshinJikokuJokenTo === '5' && (
                     <input
                       data-testid="saishu-koshin-jikoku-to-input"
                       type="datetime-local"
                       value={searchParams.saishuKoshinJikokuTo}
                       onChange={(e) => handleSearchParamChange('saishuKoshinJikokuTo', e.target.value)}
                       style={styles.datetimeInput}
                       onFocus={(e) => {
                         e.target.style.outline = styles.datetimeInputFocus.outline;
                         e.target.style.borderColor = styles.datetimeInputFocus.borderColor;
                       }}
                       onBlur={(e) => {
                         e.target.style.outline = '';
                         e.target.style.borderColor = '';
                       }}
                     />
                   )}
                   <select
                     data-testid="saishu-koshin-jikoku-joken-to-select"
                     value={searchParams.saishuKoshinJikokuJokenTo}
                     onChange={(e) => handleSearchParamChange('saishuKoshinJikokuJokenTo', e.target.value)}
                     style={styles.select}
                     onFocus={(e) => {
                       e.target.style.outline = styles.selectFocus.outline;
                       e.target.style.borderColor = styles.selectFocus.borderColor;
                     }}
                     onBlur={(e) => {
                       e.target.style.outline = '';
                       e.target.style.borderColor = '';
                     }}
                   >
                     <option value="1">指定なし</option>
                     <option value="2">現在時刻まで</option>
                     <option value="5">日時指定まで</option>
                   </select>
                 </div>
              </div>
              
              {/* ソート条件 */}
              <div style={styles.formGroup}>
                <div style={styles.detailTitle}>ソート条件</div>
                <label style={styles.label}>ソート順</label>
                <select
                  data-testid="sort-select"
                  value={searchParams.sort}
                  onChange={(e) => handleSearchParamChange('sort', e.target.value)}
                  style={styles.select}
                  onFocus={(e) => {
                    e.target.style.outline = styles.selectFocus.outline;
                    e.target.style.borderColor = styles.selectFocus.borderColor;
                  }}
                  onBlur={(e) => {
                    e.target.style.outline = '';
                    e.target.style.borderColor = '';
                  }}
                >
                  <option value="1">ID</option>
                  <option value="2">更新時刻</option>
                </select>
              </div>
              
              {/* 登録状況 */}
              <div style={styles.formGroup}>
                <div style={styles.detailTitle}>登録状況</div>
                <div style={styles.checkboxContainer}>
                  <div style={styles.checkboxItem}>
                    <input
                      type="checkbox"
                      id="noImageRegist"
                      data-testid="no-image-regist-checkbox"
                      checked={searchParams.noImageRegist}
                      onChange={(e) => handleSearchParamChange('noImageRegist', e.target.checked)}
                      style={styles.checkbox}
                    />
                    <label htmlFor="noImageRegist" style={styles.checkboxLabel}>
                      画像未撮影
                    </label>
                  </div>
                  <div style={styles.checkboxItem}>
                    <input
                      type="checkbox"
                      id="imageRegist"
                      data-testid="image-regist-checkbox"
                      checked={searchParams.imageRegist}
                      onChange={(e) => handleSearchParamChange('imageRegist', e.target.checked)}
                      style={styles.checkbox}
                    />
                    <label htmlFor="imageRegist" style={styles.checkboxLabel}>
                      画像撮影済
                    </label>
                  </div>
                  <div style={styles.checkboxItem}>
                    <input
                      type="checkbox"
                      id="noSatsueiMemoRegist"
                      data-testid="no-satsuei-memo-regist-checkbox"
                      checked={searchParams.noSatsueiMemoRegist}
                      onChange={(e) => handleSearchParamChange('noSatsueiMemoRegist', e.target.checked)}
                      style={styles.checkbox}
                    />
                    <label htmlFor="noSatsueiMemoRegist" style={styles.checkboxLabel}>
                      商品情報未登録
                    </label>
                  </div>
                </div>
               </div>
               
               {/* 検索条件クリアボタン */}
               <div style={styles.clearGroup}>
                 <button
                   style={styles.clearButton}
                   onClick={() => {
                     setSearchParams({
                       objectId: '',
                       objectIdFrom: '',
                       objectIdTo: '',
                       satsueiMemoCategory: '',
                       kyoten: '',
                       shokiTorokusha: '',
                       shokiTorokuJikokuFrom: '',
                       shokiTorokuJikokuTo: '',
                       shokiTorokuJikokuJokenFrom: '1',
                       shokiTorokuJikokuJokenTo: '1',
                       saishuKoshinsha: '',
                       saishuKoshinJikokuFrom: '',
                       saishuKoshinJikokuTo: '',
                       saishuKoshinJikokuJokenFrom: '1',
                       saishuKoshinJikokuJokenTo: '1',
                       sort: '1',
                       noImageRegist: false,
                       imageRegist: false,
                       noSatsueiMemoRegist: false
                     });

                   }}
                   onMouseEnter={(e) => {
                     e.target.style.background = styles.clearButtonHover.background;
                     e.target.style.borderColor = styles.clearButtonHover.borderColor;
                   }}
                   onMouseLeave={(e) => {
                     e.target.style.background = styles.clearButton.background;
                     e.target.style.borderColor = styles.clearButton.borderColor;
                   }}
                 >
                   検索条件クリア
                 </button>
               </div>
             </div>
           )}
           
           {/* 検索ボタン */}
          <button
            style={{
              ...styles.searchButton,
              ...(loading ? styles.searchButtonDisabled : {})
            }}
            onClick={() => handleSearch()}
            disabled={loading}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.background = styles.searchButtonHover.background;
                e.target.style.transform = styles.searchButtonHover.transform;
                e.target.style.boxShadow = styles.searchButtonHover.boxShadow;
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.background = styles.searchButton.background;
                e.target.style.transform = '';
                e.target.style.boxShadow = styles.searchButton.boxShadow;
              }
            }}
            onMouseDown={(e) => {
              if (!loading) {
                e.target.style.transform = styles.searchButtonActive.transform;
                e.target.style.boxShadow = styles.searchButtonActive.boxShadow;
              }
            }}
            onMouseUp={(e) => {
              if (!loading) {
                e.target.style.transform = styles.searchButtonHover.transform;
                e.target.style.boxShadow = styles.searchButtonHover.boxShadow;
              }
            }}
          >
            検索
          </button>
        </div>
        
        {/* 検索結果 */}
        <div data-scroll-container style={{ height: '100%', overflowY: 'scroll', WebkitOverflowScrolling: 'touch' }}>
          <div style={styles.objectList}>
            {searchResults.map((result, index) => (
              <div
                key={result.objectId || index}
                style={styles.objectDIV}
                onClick={() => handleResultClick(result.objectId)}
              >
                <div style={styles.resultTitle}>{result.objectId}</div>
                <div style={styles.resultInfo}>
                  <img 
                    src={imageUrls[result.objectId] || '/core/img/errorimage.png'}
                    alt={`Object ${result.objectId}`}
                    style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain' }}
                    onError={(e) => {
                      e.target.src = '/core/img/errorimage.png';
                    }}
                  />
                </div>
              </div>
            ))}
          </div>  
          {hasMore && (
            <div style={styles.loadObjectDataButtonArea}>
              <button 
                style={styles.loadObjectDataButton}
                onClick={handleLoadMore}
              >
              さらに読み込む
              </button>
            </div>
          )}
        </div>
        </div>
      )}
      
      <Footer />
      
      {/* エラーダイアログ */}
      <MessageDialog
        isOpen={showErrorDialog}
        onClose={closeErrorDialog}
        title="エラー"
        message={errorMessage}
        type={messageType}
      />
    </div>
  );
};

export default AppSearch;
